import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_learn-assessments.scss';
import '../styles/pages/_quiz-completion.scss';

interface QuizQuestion {
  question: string;
  options: string[];
  correctAnswer: number;
  explanation: string;
}

const quizQuestions: QuizQuestion[] = [
  {
    question: "According to the energy management framework, which time of day is best for focused work and movement?",
    options: [
      "Morning (6-12)",
      "Afternoon (12-6)",
      "Evening (6-12)",
      "Late night (12-6)"
    ],
    correctAnswer: 0,
    explanation: "Morning (6-12) is described as a natural energy peak, making it ideal for movement and focused work. This aligns with our body's natural circadian rhythms."
  },
  {
    question: "What is the recommended weekly goal for moderate-intensity physical activity?",
    options: [
      "60 minutes per week",
      "90 minutes per week",
      "150 minutes per week",
      "300 minutes per week"
    ],
    correctAnswer: 2,
    explanation: "150 minutes of moderate-intensity activity per week is the established health guideline. This can be broken down into smaller sessions throughout the week for better consistency."
  },
  {
    question: "Which of the following is NOT recommended for breaking up prolonged sitting?",
    options: [
      "Stand and stretch for 1-2 minutes every hour",
      "Take walking meetings",
      "Sit more comfortably with better cushions",
      "Use a standing desk occasionally"
    ],
    correctAnswer: 2,
    explanation: "While comfortable seating is nice, it doesn't address the core issue of prolonged sitting. The other options actively break up sitting time, which is more effective for health."
  },
  {
    question: "What does the '20-20 Move Rule' recommend?",
    options: [
      "Move for 20 minutes every 2 hours",
      "Move for 20 seconds every 20 minutes of sitting",
      "Walk 20,000 steps daily",
      "Do 20 different exercises"
    ],
    correctAnswer: 1,
    explanation: "The 20-20 Move Rule is a practical strategy: every 20 minutes of sitting, move for 20 seconds. This helps maintain circulation and prevents the negative effects of prolonged sitting."
  },
  {
    question: "Which approach to physical wellness focuses on working with your body's natural energy cycles rather than fighting them?",
    options: [
      "Time management",
      "Energy management",
      "Task management",
      "Schedule management"
    ],
    correctAnswer: 1,
    explanation: "Energy management recognizes that our energy naturally fluctuates throughout the day and works with these cycles rather than trying to maintain constant high energy against our biology."
  }
];

const PhysicalModule3QuizPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, isLoading, saveProgress } = useModuleProgress('physicalM3');
  const [currentQuestion, setCurrentQuestion] = useState<number>(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [responses, setResponses] = useState<{[key: number]: number}>({});
  const [showCompletion, setShowCompletion] = useState(false);

  const handleAnswerSelect = (answerIndex: number) => {
    if (responses[currentQuestion] === undefined) {
      setSelectedAnswer(answerIndex);
      setShowExplanation(true);
      setResponses(prev => ({...prev, [currentQuestion]: answerIndex}));
    }
  };

  const handleNext = () => {
    if (currentQuestion < quizQuestions.length - 1) {
      const nextQuestion = currentQuestion + 1;
      setCurrentQuestion(nextQuestion);
      setSelectedAnswer(null);
      setShowExplanation(false);
      saveProgress(nextQuestion + 1);
    } else {
      saveProgress(quizQuestions.length, true);
      setShowCompletion(true);
    }
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      const prevQuestion = currentQuestion - 1;
      setCurrentQuestion(prevQuestion);
      setSelectedAnswer(responses[prevQuestion] || null);
      setShowExplanation(responses[prevQuestion] !== undefined);
    }
  };

  const correctAnswers = Object.values(responses).filter((answer, index) =>
    answer === quizQuestions[index].correctAnswer
  ).length;

  if (isLoading) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Loading quiz...</p>
      </div>
    );
  }

  if (showCompletion) {
    return (
      <div className="quiz-completion-section">
        <h2 className="completion-title">
          <span>Congratulations!</span>
        </h2>
        <p className="completion-subtitle">
          You've successfully completed the Module 3 quiz!
        </p>
        <div className="completion-card">
          <div className="achievement-section">
            <h3 className="section-title">
              <span>Key Achievements</span>
            </h3>
            <p className="achievement-intro">You've demonstrated a great understanding of:</p>
            <ul className="achievement-list">
              <li>
                <span className="check"></span>
                <span>The foundations of energy management</span>
              </li>
              <li>
                <span className="check"></span>
                <span>Movement and rest strategies</span>
              </li>
              <li>
                <span className="check"></span>
                <span>Consistency in physical balance</span>
              </li>
            </ul>
          </div>
          <div className="next-steps-section">
            <h3 className="section-title">
              <span>Next Steps</span>
            </h3>
            <p>Continue your physical wellness journey by integrating movement and rest!</p>
            <p>Ready to deepen your understanding of energy and balance?</p>
          </div>
          <button
            className="resource-button"
            onClick={() => navigate('/panel/learn/physical-resources')}
          >
            Return to Physical Resources
          </button>
        </div>
      </div>
    );
  }

  const currentQuizQuestion = quizQuestions[currentQuestion];

  const progress = ((currentQuestion + 1) / quizQuestions.length) * 100;
  const currentQuiz = quizQuestions[currentQuestion];

  return (
    <div className="module-form">
      <div className="module-container">
        <div className="module-left physical-panel">
          <div className="module-header">
            <h1>Module 3 Quiz</h1>
            <p className="module-subtitle">Integrating Energy, Movement & Rest</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
              Let's review what we've learned in a stress-free way.
              There's no scoring - this is just to reinforce the main ideas!
            </p>
          </div>
        </div>
        <div className="assessment-right-panel">
          <div className="assessment-content">
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Question {currentQuestion + 1} of {quizQuestions.length}
              </span>
            </div>
            <div className="question-section">
              <h2 className="question-title">
                {currentQuiz.question}
              </h2>
              <p className="question-instruction">
                Please select the answer that best matches your understanding.
              </p>
              <div className="answer-options">
                {currentQuiz.options.map((option, index) => (
                  <label key={index} className="answer-option">
                    <input
                      type="radio"
                      name={`question-${currentQuestion}`}
                      value={index}
                      checked={selectedAnswer === index}
                      onChange={() => handleAnswerSelect(index)}
                      disabled={selectedAnswer !== null}
                    />
                    <span className="option-text">{option}</span>
                  </label>
                ))}
              </div>
              {showExplanation && (
                <div className="explanation">
                  <h3>{selectedAnswer === currentQuiz.correctAnswer ? 'Correct!' : 'Remember:'}</h3>
                  <p>{currentQuiz.explanation}</p>
                </div>
              )}
            </div>
            <div className="navigation-buttons">
              <button
                className="btn-secondary"
                onClick={currentQuestion === 0 ? () => navigate('/panel/learn/physical-resources') : handleBack}
              >
                {currentQuestion === 0 ? 'Exit Quiz' : 'Previous Question'}
              </button>
              {currentQuestion < quizQuestions.length - 1 ? (
                <button
                  className="btn-primary"
                  onClick={handleNext}
                  disabled={selectedAnswer === null}
                >
                  Next Question
                </button>
              ) : (
                <button
                  className="btn-primary"
                  onClick={handleNext}
                  disabled={selectedAnswer === null}
                >
                  Complete Quiz
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhysicalModule3QuizPage;

